import * as React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { round } from "lodash";
import { useDispatch } from "react-redux";
import { ENTITY_TYPES } from "../../../utils";
import getNumericValue from "../../../utils/getNumericValue";
import NumericLabel from "../../../components/NumericLabel";
import { calculateAvailableCreditLimit } from "../../../store/globalSlice";

const getSummation = (rows = []) => {
  return rows?.reduce(
    (totals, row) => {
      return {
        footage: (totals.footage += getNumericValue(row?.footage, "ft")),
        pogSkus: (totals.pogSkus += getNumericValue(row?.pogsku)),
        pogQty: (totals.pogQty += getNumericValue(row?.pogqty)),
        pogCost: (totals.pogCost += getNumericValue(row?.pogcost, "$")),
        missingSkus: (totals.missingSkus += getNumericValue(row?.missingskus)),
        adjQty: (totals.adjQty += getNumericValue(row?.adjqty)),
        adjCost: (totals.adjCost += getNumericValue(row?.adjcost, "$")),
        totalCost: (totals.totalCost += getNumericValue(row?.totalCost, "$")),
      };
    },
    {
      footage: 0,
      pogSkus: 0,
      pogQty: 0,
      pogCost: 0,
      missingSkus: 0,
      adjQty: 0,
      adjCost: 0,
      totalCost: 0,
    }
  );
};

function TableSummary(props) {
  const { rows = [], entityDetails = {}, entityType, itemLevel } = props;
  const dispatch = useDispatch();
  const memoizedTotal = React.useMemo(() => {
    if (entityType === ENTITY_TYPES.planogram) {
      if (rows.length) {
        return getSummation(rows, itemLevel);
      }
      const grandTotals = Object.keys(entityDetails).reduce(
        (totals, entity) => {
          const rows = entityDetails[entity];
          const entityWiseTotals = getSummation(rows, itemLevel, entity);
          return {
            footage: (totals.footage += getNumericValue(
              entityWiseTotals?.footage,
              "ft"
            )),
            pogSkus: (totals.pogSkus += getNumericValue(
              entityWiseTotals?.pogSkus
            )),
            pogQty: (totals.pogQty += getNumericValue(
              entityWiseTotals?.pogQty
            )),
            pogCost: (totals.pogCost += getNumericValue(
              entityWiseTotals?.pogCost,
              "$"
            )),
            missingSkus: (totals.missingSkus += getNumericValue(
              entityWiseTotals?.missingSkus
            )),
            adjQty: (totals.adjQty += getNumericValue(
              entityWiseTotals?.adjQty
            )),
            adjCost: (totals.adjCost += getNumericValue(
              entityWiseTotals?.adjCost,
              "$"
            )),
            totalCost: (totals.totalCost += getNumericValue(
              entityWiseTotals?.totalCost,
              "$"
            )),
          };
        },
        {
          footage: 0,
          pogSkus: 0,
          pogQty: 0,
          pogCost: 0,
          missingSkus: 0,
          adjQty: 0,
          adjCost: 0,
          totalCost: 0,
        }
      );
      dispatch(calculateAvailableCreditLimit(grandTotals.totalCost));
      return grandTotals;
    }
    return {
      completedCost: 0,
      pendingCost: 0,
    };
  }, [rows, entityType, entityDetails]);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 1,
        m: 1,
      }}
    >
      <Typography variant="subtitle1">
        {rows.length ? "Summary Total" : "Grand Total"}
      </Typography>
      {entityType === ENTITY_TYPES.planogram ? (
        <Grid
          container
          spacing={2}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          mt={0.05}
        >
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              Footage:&nbsp;
              <NumericLabel
                isSeparator
                value={round(memoizedTotal?.footage, 2)}
              />
              ft
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              POG SKUs:&nbsp;
              <NumericLabel
                isSeparator
                value={round(memoizedTotal?.pogSkus, 2)}
              />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              POG Qty:&nbsp;
              <NumericLabel
                isSeparator
                value={round(memoizedTotal?.pogQty, 2)}
              />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              POG Cost:&nbsp;
              <NumericLabel
                isSeparator
                isCurrency
                value={round(memoizedTotal?.pogCost, 2)}
              />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              Missing SKUs:&nbsp;
              <NumericLabel
                isSeparator
                value={round(memoizedTotal?.missingSkus, 2)}
              />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              Adj Qty:&nbsp;
              <NumericLabel
                isSeparator
                value={round(memoizedTotal?.adjQty, 2)}
              />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              Adj Cost:&nbsp;
              <NumericLabel
                isSeparator
                isCurrency
                value={round(memoizedTotal?.adjCost, 2)}
              />
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
          mt={0.05}
        >
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              Completed Cost:&nbsp;
              <NumericLabel isCurrency value={memoizedTotal?.completedCost} />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="subtitle2" sx={{ display: "flex" }}>
              Pending Cost:&nbsp;
              <NumericLabel isCurrency value={memoizedTotal?.pendingCost} />
            </Typography>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default React.memo(TableSummary);
